import { useState, useEffect, useCallback } from 'react';

const DB_NAME = 'ProductImagesDB';
const STORE_NAME = 'images';
const DB_VERSION = 1;

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = () => {
      request.result.createObjectStore(STORE_NAME);
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Manages product images stored in IndexedDB.
 * @param namespace - a prefix key to group images (e.g. 'mercury')
 */
export function useProductImages(namespace: string) {
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  // Load persisted images on mount
  useEffect(() => {
    const createdUrls: string[] = [];

    (async () => {
      try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readonly');
        const store = tx.objectStore(STORE_NAME);

        const allKeys: string[] = await new Promise((res, rej) => {
          const req = store.getAllKeys();
          req.onsuccess = () => res(req.result as string[]);
          req.onerror = () => rej(req.error);
        });

        const prefix = `${namespace}:`;
        const relevant = allKeys.filter((k) => k.startsWith(prefix));

        const urlMap: Record<string, string> = {};
        for (const fullKey of relevant) {
          const blob: Blob = await new Promise((res, rej) => {
            const req = store.get(fullKey);
            req.onsuccess = () => res(req.result);
            req.onerror = () => rej(req.error);
          });
          if (blob) {
            const url = URL.createObjectURL(blob);
            createdUrls.push(url);
            urlMap[fullKey.slice(prefix.length)] = url;
          }
        }

        setImageUrls(urlMap);
      } catch (err) {
        console.error('[useProductImages] load failed:', err);
      }
    })();

    return () => {
      createdUrls.forEach((u) => URL.revokeObjectURL(u));
    };
  }, [namespace]);

  const saveImage = useCallback(
    async (key: string, file: File) => {
      try {
        const db = await openDB();
        const tx = db.transaction(STORE_NAME, 'readwrite');
        tx.objectStore(STORE_NAME).put(file, `${namespace}:${key}`);
        await new Promise<void>((res, rej) => {
          tx.oncomplete = () => res();
          tx.onerror = () => rej(tx.error);
        });

        const newUrl = URL.createObjectURL(file);
        setImageUrls((prev) => {
          if (prev[key]) URL.revokeObjectURL(prev[key]);
          return { ...prev, [key]: newUrl };
        });
      } catch (err) {
        console.error('[useProductImages] save failed:', err);
      }
    },
    [namespace]
  );

  return { imageUrls, saveImage };
}
